class NorthNobleInteractivePassport extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') {
      this.positionInJourney();
      return;
    }
    this.dataset.ready = 'true';
    this.originalPosition = document.createComment('North & Noble passport position');
    this.parentNode?.insertBefore(this.originalPosition, this);
    this.book = this.querySelector('[data-passport-book]');
    this.leaves = [...this.querySelectorAll('[data-passport-leaf]')];
    this.prevButton = this.querySelector('[data-passport-prev]');
    this.nextButton = this.querySelector('[data-passport-next]');
    this.status = this.querySelector('[data-passport-status]');
    this.dots = [...this.querySelectorAll('[data-passport-dot]')];
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.current = 0;
    this.maxVisited = 0;
    this.drag = null;
    this.inView = false;
    this.isAnimating = false;
    this.animationTimer = null;
    this.turnDuration = this.reducedMotion.matches ? 0 : 780;

    if (!this.book || this.leaves.length < 2) return;
    this.book.classList.add('is-enhanced');
    this.leaves.forEach((leaf, index) => {
      leaf.style.zIndex = String(this.leaves.length - index);
      leaf.style.setProperty('--turn-progress', '0');
    });

    this.onPointerDown = this.pointerDown.bind(this);
    this.onPointerMove = this.pointerMove.bind(this);
    this.onPointerUp = this.pointerUp.bind(this);
    this.onKeyDown = this.keyDown.bind(this);
    this.onPrev = () => this.goTo(this.current - 1, 'button');
    this.onNext = () => this.goTo(this.current + 1, 'button');
    this.onMotionChange = () => this.sync();
    this.onMediaToggle = this.toggleMedia.bind(this);
    this.onDot = (event) => {
      const target = Number(event.currentTarget.dataset.pageIndex);
      this.goTo(target, 'progress-dot');
    };
    this.onVisibilityChange = () => {
      if (document.hidden) this.pauseAllMedia();
      this.releaseNavigationLock();
    };
    this.onViewportChange = () => {
      this.releaseNavigationLock();
      this.positionInJourney();
    };

    this.book.addEventListener('pointerdown', this.onPointerDown);
    this.book.addEventListener('pointermove', this.onPointerMove, { passive: false });
    this.book.addEventListener('pointerup', this.onPointerUp);
    this.book.addEventListener('pointercancel', this.onPointerUp);
    this.book.addEventListener('keydown', this.onKeyDown);
    this.prevButton?.addEventListener('click', this.onPrev);
    this.nextButton?.addEventListener('click', this.onNext);
    this.reducedMotion.addEventListener?.('change', this.onMotionChange);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('resize', this.onViewportChange, { passive: true });
    this.querySelectorAll('[data-passport-media-toggle]').forEach((button) => button.addEventListener('click', this.onMediaToggle));
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDot));

    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting && entry.intersectionRatio > 0.35;
      this.syncMedia();
    }, { threshold: [0, .35, .75] });
    this.observer.observe(this);
    this.positionInJourney();
    this.sync();
  }

  disconnectedCallback() {
    queueMicrotask(() => {
      if (this.isConnected) return;
      this.teardown();
    });
  }

  teardown() {
    this.book?.removeEventListener('pointerdown', this.onPointerDown);
    this.book?.removeEventListener('pointermove', this.onPointerMove);
    this.book?.removeEventListener('pointerup', this.onPointerUp);
    this.book?.removeEventListener('pointercancel', this.onPointerUp);
    this.book?.removeEventListener('keydown', this.onKeyDown);
    this.prevButton?.removeEventListener('click', this.onPrev);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.reducedMotion?.removeEventListener?.('change', this.onMotionChange);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('resize', this.onViewportChange);
    window.clearTimeout(this.animationTimer);
    this.querySelectorAll('[data-passport-media-toggle]').forEach((button) => button.removeEventListener('click', this.onMediaToggle));
    this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDot));
    this.observer?.disconnect();
    this.pauseAllMedia();
  }

  positionInJourney() {
    if (this.isPositioning || !this.originalPosition?.parentNode) return;
    this.isPositioning = true;

    const desktop = window.matchMedia('(min-width: 990px)').matches;
    const stepsWrap = document.querySelector('[data-nn-map-builder] .nn-map-builder__steps');
    const steps = stepsWrap
      ? [...stepsWrap.children].filter((node) => node.classList.contains('nn-map-builder__step'))
      : [];

    if (desktop && steps.length >= 2) {
      if (this.parentNode !== stepsWrap || this.nextElementSibling !== steps[1]) {
        stepsWrap.insertBefore(this, steps[1]);
      }
    } else if (this.parentNode !== this.originalPosition.parentNode) {
      this.originalPosition.parentNode.insertBefore(this, this.originalPosition.nextSibling);
    }

    this.isPositioning = false;
  }

  pointerDown(event) {
    if (this.reducedMotion.matches || this.isAnimating || event.button > 0) return;
    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startTime: performance.now(),
      lastX: event.clientX,
      lastTime: performance.now(),
      velocityX: 0,
      progress: 0,
      direction: null,
      locked: false,
      protectedEdge: event.pointerType === 'touch' && event.clientX < Math.min(38, window.innerWidth * .08),
      leaf: null
    };
  }

  pointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const dx = event.clientX - this.drag.startX;
    const dy = event.clientY - this.drag.startY;
    if (!this.drag.locked) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      if (Math.abs(dx) <= Math.abs(dy) * 1.25) {
        this.drag = null;
        return;
      }
      const direction = dx < 0 ? 'forward' : 'backward';
      if ((direction === 'forward' && this.current >= this.leaves.length - 1) ||
          (direction === 'backward' && (this.current <= 0 || this.drag.protectedEdge))) {
        this.drag = null;
        return;
      }
      this.drag.direction = direction;
      this.drag.leaf = direction === 'forward' ? this.leaves[this.current] : this.leaves[this.current - 1];
      this.drag.locked = true;
      this.drag.leaf.classList.add('is-dragging');
      this.drag.leaf.classList.toggle('is-reversing', direction === 'backward');
      this.book.dataset.turnDirection = direction;
      this.book.setPointerCapture?.(event.pointerId);
    }
    event.preventDefault();
    const width = Math.max(this.book.getBoundingClientRect().width, 1);
    const rawProgress = this.drag.direction === 'forward'
      ? Math.min(1, Math.max(0, -dx / (width * .9)))
      : Math.min(1, Math.max(0, 1 - (dx / (width * .9))));
    this.drag.progress = rawProgress * rawProgress * (3 - (2 * rawProgress));
    this.drag.leaf.style.setProperty('--turn-progress', String(this.drag.progress));
    const now = performance.now();
    const elapsed = Math.max(now - this.drag.lastTime, 1);
    this.drag.velocityX = (event.clientX - this.drag.lastX) / elapsed;
    this.drag.lastX = event.clientX;
    this.drag.lastTime = now;
  }

  pointerUp(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const { leaf, progress, locked, direction, velocityX } = this.drag;
    if (!locked || !leaf) {
      this.drag = null;
      return;
    }
    const wasCancelled = event.type === 'pointercancel';
    leaf.classList.remove('is-dragging');
    leaf.classList.remove('is-reversing');
    this.book.releasePointerCapture?.(event.pointerId);
    this.drag = null;
    const distance = direction === 'backward' ? 1 - progress : progress;
    const intentionalVelocity = direction === 'forward' ? velocityX < -.48 : velocityX > .48;
    if (wasCancelled || (distance < .3 && !(distance > .1 && intentionalVelocity))) {
      leaf.style.setProperty('--turn-progress', direction === 'backward' ? '1' : '0');
      delete this.book.dataset.turnDirection;
      return;
    }
    this.goTo(this.current + (direction === 'forward' ? 1 : -1), 'drag', direction);
  }

  keyDown(event) {
    if (event.target.closest('input, textarea, select, video, [contenteditable="true"]')) return;
    if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.current + 1, 'keyboard', 'forward'); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.current - 1, 'keyboard', 'backward'); }
  }

  goTo(next, source, requestedDirection) {
    if (this.isAnimating) return;
    const target = Math.min(this.leaves.length - 1, Math.max(0, next));
    if (target === this.current) return;
    const previous = this.current;
    const direction = requestedDirection || (target > previous ? 'forward' : 'backward');
    this.isAnimating = true;
    this.turnDuration = this.reducedMotion.matches ? 0 : 720;
    this.book.dataset.turnDirection = direction;
    this.pauseAllMedia();
    this.current = target;
    this.maxVisited = Math.max(this.maxVisited, target);
    this.sync(false);
    window.clearTimeout(this.animationTimer);
    this.animationTimer = window.setTimeout(() => {
      this.isAnimating = false;
      delete this.book.dataset.turnDirection;
      this.sync(true);
      const detail = {
        event: target === 0 ? 'passport_cover_returned' : 'passport_page_completed',
        country: this.leaves[target]?.dataset.pageName || '',
        page_index: target,
        page_total: this.leaves.length - 1,
        direction,
        source
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(detail);
      this.dispatchEvent(new CustomEvent('nn:passport-page-change', { bubbles: true, detail }));
    }, this.turnDuration);
  }

  sync(announce = true) {
    this.leaves.forEach((leaf, index) => {
      const turned = index < this.current;
      leaf.classList.toggle('is-turned', turned);
      leaf.classList.toggle('is-current', index === this.current);
      leaf.style.setProperty('--turn-progress', turned ? '1' : '0');
      leaf.style.zIndex = turned ? String(index) : String(this.leaves.length - index);
      leaf.setAttribute('aria-hidden', String(index !== this.current));
    });
    this.book.classList.toggle('is-open', this.current > 0);
    if (this.prevButton) this.prevButton.disabled = this.isAnimating || this.current === 0;
    if (this.nextButton) {
      this.nextButton.disabled = this.isAnimating || this.current === this.leaves.length - 1;
      const label = this.nextButton.querySelector('span');
      if (label) label.textContent = this.current === 0 ? 'Open' : this.current === this.leaves.length - 2 ? 'Finish' : 'Next';
    }
    this.dots?.forEach((dot, index) => {
      const current = index === this.current;
      dot.toggleAttribute('disabled', this.isAnimating);
      dot.classList.toggle('is-visited', index <= this.maxVisited);
      if (current) dot.setAttribute('aria-current', 'step');
      else dot.removeAttribute('aria-current');
    });
    const pageName = this.leaves[this.current]?.dataset.pageName || '';
    if (announce && this.status) this.status.textContent = `${pageName} · ${this.current + 1} of ${this.leaves.length}`;
    if (!this.isAnimating) this.syncMedia();
  }

  releaseNavigationLock() {
    if (this.drag?.leaf) {
      this.drag.leaf.classList.remove('is-dragging', 'is-reversing');
      this.drag.leaf.style.setProperty('--turn-progress', this.drag.direction === 'backward' ? '1' : '0');
    }
    this.drag = null;
    if (!this.isAnimating) {
      delete this.book.dataset.turnDirection;
      return;
    }
    window.clearTimeout(this.animationTimer);
    this.isAnimating = false;
    delete this.book.dataset.turnDirection;
    this.sync(true);
  }

  loadVideo(video) {
    if (video.dataset.loaded === 'true') return;
    video.querySelectorAll('source[data-src]').forEach((source) => { source.src = source.dataset.src; });
    video.dataset.loaded = 'true';
    video.load();
    video.addEventListener('canplay', () => video.classList.add('is-ready'), { once: true });
  }

  syncMedia() {
    const activeVideo = this.leaves[this.current]?.querySelector('video');
    this.querySelectorAll('video').forEach((video) => {
      if (video !== activeVideo) { video.pause(); return; }
      this.loadVideo(video);
      if (this.inView && !this.reducedMotion.matches) video.play().catch(() => {});
    });
  }

  pauseAllMedia() { this.querySelectorAll('video').forEach((video) => video.pause()); }

  toggleMedia(event) {
    event.stopPropagation();
    const video = event.currentTarget.closest('.nn-passport__media')?.querySelector('video');
    if (!video) return;
    if (video.paused) {
      this.loadVideo(video);
      video.play().catch(() => {});
      event.currentTarget.textContent = 'Ⅱ';
      event.currentTarget.setAttribute('aria-label', 'Pause destination film');
    } else {
      video.pause();
      event.currentTarget.textContent = '▷';
      event.currentTarget.setAttribute('aria-label', 'Play destination film');
    }
  }
}

if (!customElements.get('north-noble-interactive-passport')) {
  customElements.define('north-noble-interactive-passport', NorthNobleInteractivePassport);
}
